'use client';

import { AdSlot, type AdSlotProps } from '@/app_components/ads/Adslot';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import { SLOTS_LIMIT } from '@/app_lib/htmlParser/inserter/rules/dotCom/embedAdSlots';
import { useAdContext } from '@/app_lib/providers/AdProvider';
import { usePaywall } from '@/app_lib/providers/PaywallProvider';
import { AnalyticsEvent, ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

interface InstreamAdProps extends AdSlotProps {
  className?: string;
  dataCy?: string;
  isMobileAd?: boolean;
  isDesktopAd?: boolean;
  desktopAdIndex?: number;
}

/**
 * Renders an in-stream advertisement slot within the content.
 *
 * @param {AdSlotProps} props - The properties for the ad slot.
 * @param {number} [props.index=0] - The index of the ad slot (0-indexed).
 * @param {string} props.placementName - The name of the ad placement.
 * @returns The rendered ad slot component, or null if ads should be hidden or configuration is missing.
 */
export const InStream = ({
  index = 0,
  desktopAdIndex = 0,
  placementName,
  className = '',
  dataCy = ContentPlacement.InStream,
  isMobileAd = true,
  isDesktopAd = true,
}: InstreamAdProps) => {
  const { pageAdConfig, hideInStreamAds } = useAdContext();
  const {
    paywallState: { paywallCheck },
  } = usePaywall();

  const isMobile = useMediaQuery(Breakpoints.Mobile);

  let adIndex = index;
  if (!isMobile) {
    adIndex = desktopAdIndex;
  }

  // We are creating the ads for both mobile and desktop on the server
  // On the client, we delete the one that isn't necessary (e.g. delete desktop ads on mobile and vice versa)
  const dontShowOnMobile = isMobile && (!isMobileAd || adIndex >= SLOTS_LIMIT);
  const dontShowOnDesktop = !isMobile && (!isDesktopAd || adIndex >= SLOTS_LIMIT);

  // This is to remove ads that shouldn't be shown on mobile or desktop
  if (dontShowOnDesktop || dontShowOnMobile) {
    // the data-empty true is being targetted in the inserter so it can hide the element and prevent flex gaps
    return <div data-empty={true} />;
  }

  if (!pageAdConfig || hideInStreamAds || paywallCheck === 'paywalled') {
    return (
      <div
        className={cn('contents h-[1px] w-[1px]', AnalyticsEvent.ImpressionElement)}
        data-cy={`${dataCy}-hidden-${adIndex}`}
      />
    );
  }

  return (
    <div className='flex justify-center items-center w-full'>
      <div
        className={cn('absolute h-[1px] w-[1px]', AnalyticsEvent.ImpressionElement)}
        data-cy={`${dataCy}-${adIndex}`}
      />
      <div className={cn('text-center min-h-[50px]', className)}>
        <AdSlot
          index={adIndex}
          placementName={placementName}
        />
      </div>
    </div>
  );
};
